'use client';

/**
 * useReverseGeocodeOnIdle — bắt sự kiện di chuyển map (pan) và reverse-geocode
 * toạ độ TÂM map khi map dừng (idle). Dùng cho luồng "pin cố định giữa + kéo
 * map": mũi pin luôn ở tâm nên chỉ cần reverse toạ độ tâm sau mỗi lần idle.
 *
 * - `isMoving`: đang pan (moveStart → moveEnd) → CenterPin nhấc lên, ẩn địa chỉ.
 * - `isLoading`: đang gọi reverse API → hiện skeleton "Đang tìm địa chỉ...".
 * - `address`: kết quả Address gần nhất (giữ giá trị cũ khi đang tải/khi lỗi).
 * - reqId guard: bỏ qua response cũ nếu user pan tiếp trước khi API trả về.
 */
import type { Address } from '@ahm/api-wrappers-core';
import { useCallback, useRef, useState } from 'react';
import type { ViewStateChangeEvent } from 'react-map-gl/maplibre';
import { getGeocodeReverseFn } from '@/api/ahafood/get-revert-address-fn';

export type UseReverseGeocodeOnIdleResult = {
  address: Address | null;
  isMoving: boolean;
  isLoading: boolean;
  /** Gán vào <MapView onMoveStart> */
  onMoveStart: () => void;
  /** Gán vào <MapView onMoveEnd> */
  onMoveEnd: (e: ViewStateChangeEvent) => void;
  /** Reverse thủ công 1 toạ độ (vd sau khi flyTo tới vị trí GPS/search). */
  reverse: (lat: number, lng: number) => Promise<Address | null>;
  /** Set address trực tiếp (vd hydrate từ địa chỉ đã chọn trước). */
  setAddress: (address: Address | null) => void;
};

export function useReverseGeocodeOnIdle(
  initialAddress: Address | null = null
): UseReverseGeocodeOnIdleResult {
  const [address, setAddress] = useState<Address | null>(initialAddress);
  const [isMoving, setIsMoving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const reqIdRef = useRef(0);

  const reverse = useCallback(async (lat: number, lng: number) => {
    const reqId = ++reqIdRef.current;
    setIsLoading(true);
    try {
      const result = await getGeocodeReverseFn({ lat, lng });
      if (reqId === reqIdRef.current) {
        setAddress(result);
        return result;
      }
    } catch (error) {
      console.error('❌ reverse-geocode-on-idle failed:', error);
    } finally {
      if (reqId === reqIdRef.current) {
        setIsLoading(false);
      }
    }
    return null;
  }, []);

  const onMoveStart = useCallback(() => {
    setIsMoving(true);
  }, []);

  const onMoveEnd = useCallback(
    (e: ViewStateChangeEvent) => {
      setIsMoving(false);
      const { latitude, longitude } = e.viewState;
      void reverse(latitude, longitude);
    },
    [reverse]
  );

  return {
    address,
    isMoving,
    isLoading,
    onMoveStart,
    onMoveEnd,
    reverse,
    setAddress,
  };
}
